import { useContext, useRef } from "react";

type Mode = "create" | "reuse" | "auto";
/**
| Mode                 | Behavior                     |
| -------------------- | ---------------------------- |
| `"create"` (default) | always new store             |
| `"reuse"`            | must use parent store        |
| `"auto"`             | reuse if exists, else create |
**/

export function provideContextStore(
  storeModule: {
    StoreContext: any;
    createStores: () => any;
  },
  Component: React.ComponentType<any>,
  options?: { mode?: Mode }
) {
  const { StoreContext, createStores } = storeModule;
  const mode = options?.mode ?? "create";

  function Wrapped(props: any) {
    const parentStores = useContext(StoreContext);
    const ref = useRef<any>(null);

    // 🔥 REUSE mode
    if (mode === "reuse") {
      if (!parentStores) {
        throw new Error(
          "[provideContextStore] No parent store found for reuse mode"
        );
      }
      return <Component {...props} />;
    }

    // 🔥 AUTO mode
    if (mode === "auto" && parentStores) {
      return <Component {...props} />;
    }

    // 🔥 CREATE mode
    if (!ref.current) {
      ref.current = createStores();
    }

    return (
      <StoreContext.Provider value={ref.current}>
        <Component {...props} />
      </StoreContext.Provider>
    );
  }

  return Wrapped;
}
